import React from 'react';
import removeIcon from '../../../img/Excluir.svg';
import playIcon from '../../../img/go.svg';

export type MediaGridItem = {
  key: string;
  kind: 'local' | 'uploaded';
  url: string;
  mimeType?: string;
  name?: string;
  uploadedId?: string;
};

interface MediaGridProps {
  items: MediaGridItem[];
  disabled?: boolean;
  onRemove: (item: MediaGridItem) => void;
  /**
   * Seleção em massa (at-58). Quando ligada, toda thumb de mídia já enviada
   * exibe uma caixa de seleção — sempre visível, sem modo a ativar (decisão do
   * usuário, 06/08/2026). Marcar é só pela caixa: o clique na imagem continua
   * abrindo o arquivo, e o X de remoção individual continua existindo.
   */
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (item: MediaGridItem) => void;
}

const isVideo = (mimeType?: string, url?: string) => {
  if (mimeType && mimeType.startsWith('video/')) return true;
  return (url || '').toLowerCase().endsWith('.mp4');
};

const MediaGrid: React.FC<MediaGridProps> = ({
  items,
  disabled,
  onRemove,
  selectable,
  selectedIds,
  onToggleSelect,
}) => {
  if (items.length === 0) {
    return <div className="checklist-grid-empty">Nenhuma mídia adicionada ainda.</div>;
  }

  return (
    <div className="checklist-grid">
      {items.map((it) => {
        const video = isVideo(it.mimeType, it.url);
        // Só mídia já enviada tem id para selecionar; arquivo ainda não enviado
        // sai do lote pelo X normal (nem chegou ao servidor).
        const selecionavel = Boolean(selectable && it.kind === 'uploaded' && it.uploadedId);
        const selecionada = selecionavel && Boolean(selectedIds?.has(it.uploadedId as string));
        const abrirMidia = () => window.open(it.url, '_blank', 'noopener,noreferrer');

        return (
          <div
            key={it.key}
            className={`checklist-thumb${selecionada ? ' selecionada' : ''}`}
          >
            <div className="checklist-thumb-media">
              {selecionavel && (
                <label className="checklist-thumb-select" title={selecionada ? 'Desmarcar' : 'Selecionar'}>
                  <input
                    type="checkbox"
                    checked={selecionada}
                    onChange={() => onToggleSelect?.(it)}
                    disabled={disabled}
                    aria-label={`Selecionar ${it.name || 'mídia'}`}
                  />
                </label>
              )}
              {video ? (
                <>
                  <video
                    className="checklist-thumb-video"
                    src={it.url}
                    controls={false}
                    preload="metadata"
                    onClick={abrirMidia}
                    style={{ cursor: 'pointer' }}
                  />
                  <img className="checklist-thumb-play" src={playIcon} alt="" aria-hidden="true" />
                </>
              ) : (
                <img
                  className="checklist-thumb-img"
                  src={it.url}
                  alt={it.name || 'Imagem do checklist'}
                  onClick={abrirMidia}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </div>
            <div className="checklist-thumb-footer">
              <div className="checklist-thumb-name" title={it.name || ''}>
                {it.name || (it.kind === 'uploaded' ? 'Arquivo' : 'Novo arquivo')}
              </div>
              <button
                type="button"
                className="checklist-thumb-remove"
                onClick={() => onRemove(it)}
                disabled={disabled}
                aria-label="Remover mídia"
                title="Remover"
              >
                <img src={removeIcon} alt="" aria-hidden="true" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MediaGrid;
